

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTripSubOptionModal from 'components/NewTripSubOptionModal';

const TripSubOptions = () => {
    const [subOptions, setSubOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('trip_sub_options');

    useEffect(() => {
        fetchSubOptions();
    }, []);

    const fetchSubOptions = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/trip-sub-options/');
            setSubOptions(response.data);
        } catch (error) {
            console.error('Error fetching trip sub-options:', error);
            setSnackbar({ open: true, message: 'Error loading trip sub-options', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveSubOption = async (payload) => {
        try {
            await axiosInstance.post('/trip-sub-options/', payload);
            setSnackbar({ open: true, message: 'Trip Sub-Option created successfully', severity: 'success' });
            fetchSubOptions();
        } catch (error) {
            console.error('Error saving trip sub-option:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/trip-sub-options/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Sub-Option Name",
            accessor: "name",
            width: "20%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Title", accessor: "title", width: "20%" },
        {
            Header: "Trip Option",
            accessor: "option_id",
            width: "15%",
            Cell: ({ row }) => row.original.option?.name || row.original.option_id
        },
        { Header: "Status", accessor: "status", width: "10%" },
        {
            Header: "Price",
            accessor: "price",
            width: "10%",
            Cell: ({ value, row }) => `${row.original.currency} ${value || 0}`
        },
        { Header: "Allocated", accessor: "allocation", width: "10%" },
        { Header: "Sold", accessor: "sold", width: "10%" },
        { Header: "Available", accessor: "available", width: "10%" },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: subOptions,
    }), [subOptions]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Trip Sub-Options</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Sub-Option
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : subOptions.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Trip Sub-Option" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTripSubOptionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveSubOption}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TripSubOptions;
